import { graphql, Link } from 'gatsby';
import Img from 'gatsby-image';
import { darken } from 'polished';

import * as _ from 'lodash';
import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Helmet } from 'react-helmet';

import Footer from '../components/Footer';
import SiteNav from '../components/header/SiteNav';
import PostCard from '../components/PostCard';
import PostContent from '../components/PostContent';

import ReadNextCard from '../components/ReadNextCard';
import BackToTop from '../components/BackToTop';
import Wrapper from '../components/Wrapper';
import IndexLayout from '../layouts';
import { colors } from '../styles/colors';
import { inner, outer, SiteHeader, SiteMain } from '../styles/shared';
import config from '../website-config';
import { articleSchema } from '../website-schema';

const PostTemplate = css`
  .site-main {
    background: #fff;
    padding-bottom: 4vw;
  }
`;

export const PostFull = css`
  position: relative;
  z-index: 50;
`;

export const NoImage = css`
  .post-full-content {
    padding-top: 0;
  }

  .post-full-content:before,
  .post-full-content:after {
    display: none;
  }
`;

export const PostFullHeader = styled.header`
  margin: 0 auto;
  padding: 6vw 3vw 3vw;
  max-width: 1040px;
  text-align: center;

  @media (max-width: 500px) {
    padding: 14vw 3vw 10vw;
  }
`;

const PostFullMeta = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.midgrey};
  font-size: 1.4rem;
  font-weight: 600;
  text-transform: uppercase;

  a {
    text-decoration: none;
  }

  @media (max-width: 500px) {
    font-size: 1.2rem;
    line-height: 1.3em;
    margin-bottom: 0.75rem;
  }
`;

export const PostFullTitle = styled.h1`
  margin: 0;
  color: ${colors.omega};
  @media (max-width: 500px) {
    font-size: 2.3rem;
  }
`;

const PostFullImage = styled.figure`
  margin: 0 -10vw 0;
  height: 700px;
  background: transparent center center;
  background-size: cover;
  border-radius: 5px;

  @media (max-width: 1170px) {
    margin: 0 -4vw -100px;
    height: 600px;
    border-radius: 0;
  }

  @media (max-width: 800px) {
    height: 400px;
  }
  @media (max-width: 500px) {
    margin-bottom: 4vw;
    height: 350px;
  }
`;



const SlashDivider = styled.span`
  display: inline-block;
  margin: 0 6px 1px;
`;

const ReadNextFeed = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -20px;
  padding: 40px 0 0 0;
`;

const TagList = styled.div`
  font-size: 1.3rem;
  font-weight: bold;
  margin-top: 2rem;
  a {
    text-decoration: none;
    text-transform: uppercase;
    margin: 0 4px;
    border: 1px solid ${colors.beta};
    border-radius: 4px;
    padding: 4px 8px;
    transition: border-color 0.15s ease-out;
  }
  a:hover {
    text-decoration: none;
    border-color: ${colors.alpha};
  }
`;

interface PageTemplateProps {
  pathContext: {
    slug: string;
  };
  data: {
    markdownRemark: {
      html: string;
      htmlAst: any;
      excerpt: string;
      timeToRead: string;
      fields: {
        slug: string;
      };
      frontmatter: {
        title: string;
        date: string;
        userDate: string;
        image: {
          childImageSharp: {
            fluid: any;
          };
        };
        tags: string[];
        intro: string;
        meta_description: string;
        photo_info: {
          camera: string;
          iso: string;
          aperture: string;
          shutter: string;
          focal?: string;
          source?: string;
          photographer: {
            name?: string;
            url?: string;
          };
        };
        slider: {
          before: {
            childImageSharp: {
              fluid: any;
            };
          };
          after: {
            childImageSharp: {
              fluid: any;
            };
          };
          thin: boolean;
        };
        // author: {
        //   id: string;
        //   bio: string;
        //   avatar: {
        //     children: {
        //       fixed: {
        //         src: string;
        //       };
        //     }[];
        //   };
        // };
      };
    };
    relatedPosts: {
      totalCount: number;
      edges: {
        node: {
          timeToRead: number;
          frontmatter: {
            title: string;
          };
          fields: {
            slug: string;
          };
        };
      }[];
    };
  };
  pageContext: {
    prev: PageContext;
    next: PageContext;
  };
}

export interface PageContext {
  excerpt: string;
  timeToRead: number;
  fields: {
    slug: string;
  };
  frontmatter: {
    image: {
      childImageSharp: {
        fluid: any;
      };
    };
    title: string;
    date: string;
    tags: string[];
    intro: string;
    meta_description: string;
    photo_info: {
      camera: string;
      iso: string;
      aperture: string;
      shutter: string;
      focal?: string;
      source?: string;
      photographer: {
        name?: string;
        url?: string;
      };
    };
    slider: {
      before: {
        childImageSharp: {
          fluid: any;
        };
      };
      after: {
        childImageSharp: {
          fluid: any;
        };
      };
      thin: boolean;
    };
    // author: {
    //   id: string;
    //   bio: string;
    //   avatar: {
    //     children: {
    //       fixed: {
    //         src: string;
    //       };
    //     }[];
    //   };
    // };
  };
}

interface PageTemplateState {
  isOpen: boolean;
}

class PageTemplate extends React.Component<PageTemplateProps, PageTemplateState> {

  constructor(props: PageTemplateProps) {
    super(props);
    this.state = { isOpen: false };
  }


  render() {
    const post = this.props.data.markdownRemark;
    let width = '';
    let height = '';
    if (post.frontmatter.image) {
      width = post.frontmatter.image.childImageSharp.fluid.sizes.split(', ')[1].split('px')[0];
      height = String(Number(width) / post.frontmatter.image.childImageSharp.fluid.aspectRatio);
    }

    return (
      <IndexLayout className="post-template">
        <Helmet>
          <title>{post.frontmatter.title} &bull; Giggster Guide</title>
          <meta name="description" content={post.frontmatter.meta_description} />

          <meta property="og:site_name" content={config.title} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={post.frontmatter.title} />
          <meta property="og:description" content={post.frontmatter.meta_description} />
          <meta property="og:url" content={config.siteUrl + this.props.pathContext.slug} />
          {post.frontmatter.image && (
            <meta
              property="og:image"
              content={`https://giggster.com/guide${post.frontmatter.image.childImageSharp.fluid.src}`}
            />
          )}
          <meta property="article:published_time" content={post.frontmatter.date} />
          {/* not sure if modified time possible */}
          {/* <meta property="article:modified_time" content="2018-08-20T15:12:00.000Z" /> */}
          {post.frontmatter.tags && (
            <meta property="article:tag" content={post.frontmatter.tags[0]} />
          )}

          <meta property="article:publisher" content={config.facebook} />
          {/* <meta property="article:author" content={config.facebook} /> */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.frontmatter.title} />
          <meta name="twitter:description" content={post.frontmatter.meta_description} />
          <meta name="twitter:url" content={config.siteUrl + this.props.pathContext.slug} />
          {post.frontmatter.image && (
            <meta
              name="twitter:image"
              content={`https://giggster.com/guide${post.frontmatter.image.childImageSharp.fluid.src}`}
            />
          )}
          {/* <meta name="twitter:label1" content="Written by" /> */}
          {/* <meta name="twitter:data1" content={post.frontmatter.author.id} /> */}
          <meta name="twitter:label2" content="Filed under" />
          {post.frontmatter.tags && (
            <meta name="twitter:data2" content={post.frontmatter.tags[0]} />
          )}
          
          {width && <meta property="og:image:width" content={width} />}
          {height && <meta property="og:image:height" content={height} />}

          <script type="application/ld+json">
            {JSON.stringify(
              articleSchema({
                headline: post.frontmatter.title,
                description: post.frontmatter.meta_description,
                url: config.siteUrl + this.props.pathContext.slug,
                image: post.frontmatter.image
                  ? `https://giggster.com/guide${post.frontmatter.image.childImageSharp.fluid.src}`
                  : undefined,
                datePublished: post.frontmatter.date,
              }),
            )}
          </script>
        </Helmet>
        <Wrapper css={PostTemplate}>
          <header css={[outer, SiteHeader]}>
            <div css={inner}>
              <SiteNav />
            </div>
          </header>
          <main id="site-main" className="site-main" css={[SiteMain, outer]}>
            <div css={inner}>
              <article css={[PostFull, !post.frontmatter.image && NoImage]}>
                <PostFullHeader>
                  <PostFullMeta>
                    {post.frontmatter.tags && post.frontmatter.tags.length > 0 && (
                      <>
                        <Link to={`/${_.kebabCase(post.frontmatter.tags[0])}/`}>
                          {post.frontmatter.tags[0]}
                        </Link>
                      </>
                    )}
                  </PostFullMeta>
                  <PostFullTitle>{post.frontmatter.title}</PostFullTitle>
                  {post.frontmatter.tags && post.frontmatter.tags.length > 1 && (
                    <TagList>
                      {post.frontmatter.tags
                        .filter((__, idx) => idx > 0)
                        .map((item) => (
                          <Link key={item} to={`/${_.kebabCase(item)}/`}>
                            {item}
                          </Link>
                        ))}
                    </TagList>
                  )}
                </PostFullHeader>

                {post.frontmatter.image && (
                  <PostFullImage>
                    <Img
                      style={{ height: '100%' }}
                      fluid={post.frontmatter.image.childImageSharp.fluid}
                    />
                  </PostFullImage>
                )}
                <PostContent
                  htmlAst={post.htmlAst}
                  title={post.frontmatter.title}
                  slug={post.fields.slug}
                  imgSrc={post.frontmatter.image.childImageSharp.fluid.src}
                  intro={post.frontmatter.intro}
                  before={
                    post.frontmatter.slider &&
                    post.frontmatter.slider.before.childImageSharp.fluid.src
                  }
                  after={
                    post.frontmatter.slider &&
                    post.frontmatter.slider.after.childImageSharp.fluid.src
                  }
                  thin={post.frontmatter.slider && post.frontmatter.slider.thin}
                  camera={post.frontmatter.photo_info && post.frontmatter.photo_info.camera}
                  focal={post.frontmatter.photo_info && post.frontmatter.photo_info.focal}
                  aperture={post.frontmatter.photo_info && post.frontmatter.photo_info.aperture}
                  shutter={post.frontmatter.photo_info && post.frontmatter.photo_info.shutter}
                  iso={post.frontmatter.photo_info && post.frontmatter.photo_info.iso}
                  source={post.frontmatter.photo_info && post.frontmatter.photo_info.source}
                  photographer={
                    post.frontmatter.photo_info && post.frontmatter.photo_info.photographer
                  }
                />

              </article>
            </div>
          </main>

          {/* Links to Previous/Next posts */}
          <BackToTop />
          <aside className="read-next" css={outer}>
            <div css={inner}>
              <ReadNextFeed>
                {this.props.data.relatedPosts && (
                  <ReadNextCard
                    tags={post.frontmatter.tags}
                    relatedPosts={this.props.data.relatedPosts}
                  />
                )}

                {this.props.pageContext.prev && <PostCard post={this.props.pageContext.prev} />}
                {this.props.pageContext.next && <PostCard post={this.props.pageContext.next} />}
              </ReadNextFeed>
            </div>
          </aside>
          <Footer />
        </Wrapper>
      </IndexLayout>
    );
  }
}

export default PageTemplate;

export const query = graphql`
  query($slug: String, $primaryTag: String) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      htmlAst
      excerpt
      fields {
        slug
      }
      frontmatter {
        title
        userDate: date(formatString: "D MMMM YYYY")
        date
        tags
        intro
        meta_description
        photo_info {
          camera
          iso
          aperture
          shutter
          focal
          source
          photographer {
            name
            url
          }
        }
        slider {
          thin
          before {
            childImageSharp {
              fluid(maxWidth: 1680, quality: 90) {
                ...GatsbyImageSharpFluid
              }
            }
          }
          after {
            childImageSharp {
              fluid(maxWidth: 1680, quality: 90) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        image {
          childImageSharp {
            fluid(maxWidth: 3720, quality: 90) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        #author {
        #  id
        #  bio
        #  avatar {
        #    children {
        #      ... on ImageSharp {
        #        fixed(quality: 100) {
        #          ...GatsbyImageSharpFixed
        #        }
        #      }
        #    }
        #  }
        #}
      }
    }
    relatedPosts: allMarkdownRemark(
      filter: { frontmatter: { tags: { in: [$primaryTag] }, draft: { ne: true } } }
      limit: 3
    ) {
      totalCount
      edges {
        node {
          id
          timeToRead
          excerpt
          frontmatter {
            title
            meta_description
          }
          fields {
            slug
          }
        }
      }
    }
  }
`;
